import Vue from 'vue';
import CustomAnalyticsAPI from '../../api/customAnalytics';
import {
  browserTimeZone,
  changedLayouts,
  chartKeys,
  errorDetailFor,
  errorKeyFor,
  isKeyMode,
  nextLayoutSlot,
} from '../../helper/customAnalytics';

const LAYOUT_DEBOUNCE_MS = 500;

let layoutTimer = null;
// Latest request token per chart, so a slow earlier response never overwrites a newer one.
const latestRequest = {};

export const state = {
  charts: [],
  keys: [],
  keysTruncated: false,
  // `{ key, id }` of the latest failed request; the page toasts it. `id` makes repeats distinct.
  lastError: null,
  globalInboxId: null,
  // Layouts as the backend last confirmed them: [{ id, x, y, w, h }]
  savedLayouts: [],
  uiFlags: {
    isFetching: false,
    isFetchError: false,
    isKeysLoaded: false,
    isCreating: false,
  },
  // chart id -> { isUpdating, requestError }
  chartFlags: {},
};

export const getters = {
  getCharts: $state => $state.charts,
  getChartById: $state => id => $state.charts.find(c => c.id === id),
  getKeys: $state => $state.keys,
  getKeysTruncated: $state => $state.keysTruncated,
  getUIFlags: $state => $state.uiFlags,
  getChartFlags: $state => id =>
    $state.chartFlags[id] || { isUpdating: false, requestError: false },
  getGlobalInboxId: $state => $state.globalInboxId,
  getLastError: $state => $state.lastError,
};

const layoutOf = chart => ({ id: chart.id, ...chart.layout });

let errorCounter = 0;

export const mutations = {
  SET_UI_FLAGS($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_CHARTS($state, charts) {
    $state.charts = charts;
    $state.savedLayouts = charts.map(layoutOf);
  },
  UPSERT_CHART($state, chart) {
    const exists = $state.charts.some(c => c.id === chart.id);
    $state.charts = exists
      ? $state.charts.map(c => (c.id === chart.id ? chart : c))
      : [...$state.charts, chart];
  },
  REMOVE_CHART($state, id) {
    $state.charts = $state.charts.filter(c => c.id !== id);
    $state.savedLayouts = $state.savedLayouts.filter(l => l.id !== id);
    Vue.delete($state.chartFlags, id);
  },
  SET_KEYS($state, { keys, truncated }) {
    $state.keys = keys;
    $state.keysTruncated = truncated;
  },
  SET_CHART_FLAGS($state, { id, ...flags }) {
    Vue.set($state.chartFlags, id, {
      isUpdating: false,
      requestError: false,
      ...$state.chartFlags[id],
      ...flags,
    });
  },
  SET_GLOBAL_INBOX($state, id) {
    $state.globalInboxId = id;
  },
  // Move charts to these positions (local drags and the backend's confirmed layouts).
  APPLY_LAYOUTS($state, layouts) {
    const byId = {};
    layouts.forEach(item => {
      byId[item.id] = { x: item.x, y: item.y, w: item.w, h: item.h };
    });
    $state.charts = $state.charts.map(c =>
      byId[c.id] ? { ...c, layout: byId[c.id] } : c
    );
  },
  SET_SAVED_LAYOUTS($state, layouts) {
    $state.savedLayouts = layouts;
  },
  SET_ERROR($state, key) {
    errorCounter += 1;
    $state.lastError = { key, id: errorCounter };
  },
};

const viewOf = $state => ({
  tz: browserTimeZone(),
  inbox_id: $state.globalInboxId || undefined,
});

export const actions = {
  async fetchCharts({ commit, state: $state }) {
    commit('SET_UI_FLAGS', {
      isFetching: true,
      isFetchError: false,
      fetchErrorDetail: '',
    });
    try {
      const response = await CustomAnalyticsAPI.getCharts(viewOf($state));
      commit('SET_CHARTS', response.data?.charts || []);
      commit('SET_UI_FLAGS', { isFetching: false });
    } catch (error) {
      commit('SET_UI_FLAGS', {
        isFetching: false,
        isFetchError: true,
        fetchErrorDetail: errorDetailFor(error),
      });
      commit('SET_ERROR', errorKeyFor(error));
    }
  },

  async fetchKeys({ commit }) {
    try {
      const response = await CustomAnalyticsAPI.getKeys();
      commit('SET_KEYS', {
        keys: response.data?.keys || [],
        truncated: !!response.data?.truncated,
      });
    } catch (error) {
      commit('SET_KEYS', { keys: [], truncated: false });
    }
    commit('SET_UI_FLAGS', { isKeysLoaded: true });
  },

  // Data for an unsaved chart (the Add chart preview). Not kept in state; errors go to the caller.
  async previewChart(context, body) {
    const response = await CustomAnalyticsAPI.query({
      tz: browserTimeZone(),
      inbox_ids: [],
      hidden_values: [],
      ...body,
    });
    return response.data;
  },

  async createChart({ commit, state: $state }, body) {
    commit('SET_UI_FLAGS', { isCreating: true });
    try {
      const response = await CustomAnalyticsAPI.createChart(
        { layout: nextLayoutSlot($state.charts), ...body },
        viewOf($state)
      );
      commit('UPSERT_CHART', response.data);
      commit('SET_SAVED_LAYOUTS', [
        ...$state.savedLayouts,
        layoutOf(response.data),
      ]);
      commit('SET_UI_FLAGS', { isCreating: false });
      return response.data;
    } catch (error) {
      commit('SET_UI_FLAGS', { isCreating: false });
      commit('SET_ERROR', errorKeyFor(error));
      return null;
    }
  },

  async updateChart({ commit, dispatch, state: $state }, { id, changes }) {
    const current = $state.charts.find(c => c.id === id);
    if (!current) return null;
    const body = { ...changes };
    // Hidden values belong to one metric's values (or, comparing metrics, to the metric names):
    // a different metric, list of metrics or mode starts with none hidden, and the backend
    // wants `hidden_values: []` sent along with any of those changes.
    const modeChanged =
      body.series_by !== undefined &&
      body.series_by !== (current.series_by || 'value');
    const keyChanged =
      body.metric_key !== undefined && body.metric_key !== current.metric_key;
    const keysChanged =
      Array.isArray(body.metric_keys) &&
      JSON.stringify(body.metric_keys) !== JSON.stringify(chartKeys(current));
    if ((modeChanged || keyChanged || keysChanged) && !body.hidden_values) {
      body.hidden_values = [];
    }
    const token = (latestRequest[id] || 0) + 1;
    latestRequest[id] = token;
    commit('SET_CHART_FLAGS', { id, isUpdating: true, requestError: false });
    try {
      const response = await CustomAnalyticsAPI.updateChart(
        id,
        body,
        viewOf($state)
      );
      if (latestRequest[id] !== token) return null;
      const fresh = $state.charts.find(c => c.id === id);
      // Keep the position the user just dragged to; it is saved by its own call.
      commit('UPSERT_CHART', {
        ...response.data,
        layout: fresh ? fresh.layout : response.data.layout,
      });
      commit('SET_CHART_FLAGS', { id, isUpdating: false });
      return response.data;
    } catch (error) {
      if (latestRequest[id] !== token) return null;
      commit('SET_CHART_FLAGS', { id, isUpdating: false, requestError: true });
      commit('SET_ERROR', errorKeyFor(error));
      if (error?.response?.data?.detail?.code === 'chart_not_found') {
        dispatch('fetchCharts');
      }
      return null;
    }
  },

  retryChart({ dispatch }, id) {
    return dispatch('updateChart', { id, changes: {} });
  },

  duplicateChart({ dispatch, state: $state }, id) {
    const source = $state.charts.find(c => c.id === id);
    if (!source) return null;
    // A plain chart sends only `metric_key`, as it always has; one comparing metrics sends its list.
    const metrics = isKeyMode(source)
      ? { series_by: 'key', metric_keys: [...chartKeys(source)] }
      : { metric_key: source.metric_key };
    return dispatch('createChart', {
      title: `${source.title} (copy)`.slice(0, 100),
      ...metrics,
      viz_type: source.viz_type,
      show_legend: source.show_legend !== false,
      time_range: source.time_range,
      inbox_ids: [...source.inbox_ids],
      hidden_values: [...source.hidden_values],
      layout: nextLayoutSlot(
        $state.charts,
        12,
        source.layout.w,
        source.layout.h
      ),
    });
  },

  async deleteChart({ commit }, id) {
    try {
      await CustomAnalyticsAPI.deleteChart(id);
      commit('REMOVE_CHART', id);
      delete latestRequest[id];
      return true;
    } catch (error) {
      commit('SET_ERROR', errorKeyFor(error));
      return false;
    }
  },

  // The full value list of a chart's key (for the value filter when the top 20 are not enough).
  async fetchValues({ commit, state: $state }, id) {
    const chart = $state.charts.find(c => c.id === id);
    if (!chart) return null;
    try {
      const response = await CustomAnalyticsAPI.getValues({
        key: chart.metric_key,
        time_range: chart.time_range,
        inbox_ids: $state.globalInboxId
          ? [$state.globalInboxId]
          : chart.inbox_ids,
        tz: browserTimeZone(),
      });
      return response.data;
    } catch (error) {
      commit('SET_ERROR', errorKeyFor(error));
      return null;
    }
  },

  // A drag or resize: move the cards now, send the changed ones once the user pauses.
  updateLayout({ commit, dispatch, state: $state }, layout) {
    const next = layout.map(({ i, x, y, w, h }) => ({ id: i, x, y, w, h }));
    const moved = changedLayouts($state.charts.map(layoutOf), next);
    if (!moved.length) return;
    commit('APPLY_LAYOUTS', moved);
    clearTimeout(layoutTimer);
    layoutTimer = setTimeout(() => dispatch('flushLayout'), LAYOUT_DEBOUNCE_MS);
  },

  async flushLayout({ commit, state: $state }) {
    const items = changedLayouts(
      $state.savedLayouts,
      $state.charts.map(layoutOf)
    );
    if (!items.length) return;
    try {
      const response = await CustomAnalyticsAPI.saveLayout(items);
      const layouts = response.data?.layouts || [];
      commit('APPLY_LAYOUTS', layouts);
      commit('SET_SAVED_LAYOUTS', layouts);
    } catch (error) {
      // Put every card back where the backend last had it.
      commit('APPLY_LAYOUTS', $state.savedLayouts);
      commit('SET_ERROR', errorKeyFor(error));
    }
  },

  setGlobalInbox({ commit, dispatch }, id) {
    commit('SET_GLOBAL_INBOX', id || null);
    return dispatch('fetchCharts');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
